<script>
    import "~/tailwind.css";
    import Top from "~/components/Top.svelte";
    import UploadCatcher from "~/components/UploadCatcher.svelte";
    import * as Tooltip from "~/components/ui/tooltip";

    export let filters;
    import { MetaTags } from "svelte-meta-tags";
    import { _ } from "svelte-i18n";
    import { page } from "@inertiajs/svelte";
    import { cleanTagNames } from "~/components/utils.js";

    const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://uwubox.me';

    // The footer only rides along on pages that end — landing and profiles.
    // Anything below an infinite-scroll feed gets shoved down on every page
    // append, and the footer was our single largest source of CLS (measured
    // 1.95 → 0.28 on /media_assets just by taking it and the paginator out).
    // Tenor and Pinterest ship no footer on their feeds for the same reason.
    const FOOTER_PATHS = ['/tos', '/privacy', '/guidelines'];
    $: footerPath = ($page.url || '/').split('?')[0];
    $: showFooter = footerPath === '/'
        || footerPath.startsWith('/users')
        || footerPath.startsWith('/@')
        || FOOTER_PATHS.includes(footerPath);

    // Ruby String#truncate semantics: total length ≤ len, "..." included.
    function truncate(str, len) {
        if (!str) return '';
        return str.length > len ? str.slice(0, len - 3) + '...' : str;
    }

    // Mirror of MetaTagsHelper.display_truncate — keep the two in sync.
    // Wide (CJK + fullwidth) chars count 2 display units, others 1.
    const WIDE_CHAR_RE = /\p{Script=Han}|\p{Script=Hiragana}|\p{Script=Katakana}|\p{Script=Hangul}|[　-〿＀-｠￠-￦]/u;
    function displayWidth(str) {
        let used = 0;
        for (const ch of str) used += WIDE_CHAR_RE.test(ch) ? 2 : 1;
        return used;
    }
    function displayTruncate(str, width) {
        let used = 0, out = '';
        for (const ch of str) {
            used += WIDE_CHAR_RE.test(ch) ? 2 : 1;
            if (used > width) return out.trimEnd() + '…';
            out += ch;
        }
        return out;
    }

    // Mirror of MetaTagsHelper::SERP_LABELS — keep the two in sync, the
    // client-side MetaTags overwrite what the server rendered.
    const SERP_LABELS = {
        meme:      { zh: { suffix: "相關梗圖",   crumb: "梗圖" },
                     en: { suffix: "Meme",      crumb: "Memes" } },
        copypasta: { zh: { suffix: "相關複製文", crumb: "複製文" },
                     en: { suffix: "Copypasta", crumb: "Copypasta" } },
    };
    const CJK_RE = /\p{Script=Han}|\p{Script=Hiragana}|\p{Script=Katakana}|\p{Script=Hangul}/u;

    function serpLabels(kind, text) {
        return SERP_LABELS[kind][CJK_RE.test(text) ? 'zh' : 'en'];
    }

    function breadcrumbJsonLd(crumbName, crumbPath, pageName) {
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
                { '@type': 'ListItem', position: 1, name: 'Uwubox', item: siteUrl },
                { '@type': 'ListItem', position: 2, name: crumbName, item: `${siteUrl}${crumbPath}` },
                { '@type': 'ListItem', position: 3, name: pageName },
            ],
        };
    }

    function buildAbsoluteImageUrl(path) {
        if (!path) return null;
        if (path.startsWith('http')) return path;
        return `${siteUrl}${path}`;
    }

    let title = "";
    let description = "";
    let og = {};
    let canonicalUrl = "";
    let jsonLd = null;
    let adultContent = false;

    $: {
        adultContent = false; // reset per navigation — mirror of @adult_content
        const post = $page.props.post;
        const ma = $page.props.media_asset;
        const user = $page.props.user;
        const selectedTags = $page.props.selected;
        const searchQ = $page.props.filters?.q;

        canonicalUrl = `${siteUrl}${$page.url.split('?')[0]}`;

        if (post) {
            // Post show/edit page
            const desc = (post.description || '').replace(/\s+/g, ' ').trim();
            // Mirror of PostsController#set_og_tags_for — keep the two in sync.
            // An image-only post (no copypasta text) has no meaningful post
            // caption — ignore it wholesale (title AND keywords) and fall back
            // to the first image's own caption.
            const textCaption = desc.length > 0 ? (post.caption || {}) : {};
            const postCapTitle = (textCaption.title || '').trim();
            const maCapTitle = (post.media_assets?.[0]?.caption?.title || '').trim();
            const postTitle = post.title?.trim() ? post.title.trim()
                : postCapTitle.length > 0 ? displayTruncate(postCapTitle, 36)
                : desc.length > 0 ? displayTruncate(desc, 36)
                : maCapTitle.length > 0 ? displayTruncate(maCapTitle, 36)
                : `Post #${post.id}`;
            const labels = serpLabels('copypasta', postTitle);
            title = `${postTitle} ${labels.suffix}`;
            description = truncate(desc, 160) || $_("meta.description");
            const imageUrl = buildAbsoluteImageUrl(post.media_assets?.[0]?.url);
            const authorName = post.creator?.display_name || post.creator?.name || 'Unknown';
            og = {
                type: 'article',
                title: `${postTitle} | Uwubox`,
                description,
                url: canonicalUrl,
                images: imageUrl ? [{ url: imageUrl }] : [],
                article: {
                    publishedTime: post.created_at,
                    authors: [authorName],
                    tags: post.tags?.map(t => t.name) || [],
                },
            };
            const postKeywords = [...new Set([
                ...cleanTagNames(post.tags?.map(t => t.name) || []),
                ...(textCaption.keywords || []),
            ])];
            // Max over everything the page renders — own caption, member
            // images, reply images — mirroring Post#explicit? on the server.
            // The old form only consulted an image when the post had no text
            // caption, so a safe caption hid an explicit image.
            adultContent = [post.caption?.rating,
                            ...(post.media_assets || []).map(m => m.caption?.rating),
                            ...(post.notes || []).map(n => n.rating)]
                .some(r => r === 'explicit');
            jsonLd = [{
                '@context': 'https://schema.org',
                '@type': 'Article',
                headline: postTitle,
                image: imageUrl ? [imageUrl] : [],
                author: { '@type': 'Person', name: authorName },
                datePublished: post.created_at,
                description,
                ...(postKeywords.length > 0 && { keywords: postKeywords.join(', ') }),
            }, breadcrumbJsonLd(labels.crumb, '/p', postTitle)];
        } else if (ma) {
            // Media asset show page
            const ocrText = (ma.ocr_text || '').replace(/\s+/g, ' ').trim();
            const keywords = cleanTagNames(ma.tags?.map(t => t.name) || []);
            const aiCaption = ma.caption?.caption || '';
            const captionKeywords = ma.caption?.keywords || [];
            const capTitle = (ma.caption?.title || '').trim();
            const sourceWork = (ma.caption?.source_work || '').trim();
            // Precedence mirrors MediaAssetsController#set_og_tags_for —
            // keep the two in sync.
            let maTitle = capTitle.length > 0 ? displayTruncate(capTitle, 36)
                : ocrText.length > 0 ? displayTruncate(ocrText, 36)
                : aiCaption.length > 0 ? displayTruncate(aiCaption, 36)
                : keywords.length > 0 ? displayTruncate(keywords.join(' '), 36)
                : `Image #${ma.id}`;
            // Skip long work names that would push the kind suffix off the
            // SERP slot — mirror of MediaAssetsController#set_og_tags_for.
            if (sourceWork && !maTitle.includes(sourceWork) && displayWidth(`${maTitle} | ${sourceWork}`) <= 40)
                maTitle = `${maTitle} | ${sourceWork}`;
            const labels = serpLabels('meme', maTitle);
            title = `${maTitle} ${labels.suffix}`;
            if (aiCaption.length > 0) {
                description = truncate([aiCaption, keywords.join(', ')].filter(Boolean).join(' · '), 160);
            } else if (ocrText.length > 0 && keywords.length > 0) {
                description = truncate(`${truncate(ocrText, 120)} · ${keywords.join(', ')}`, 160);
            } else if (ocrText.length > 0) {
                description = truncate(ocrText, 160);
            } else if (keywords.length > 0) {
                description = `Tagged: ${truncate(keywords.join(', '), 140)}`;
            } else {
                description = $_("meta.description");
            }
            const imageUrl = buildAbsoluteImageUrl(ma.url);
            const creatorName = ma.creator?.display_name || ma.creator?.name || 'Unknown';
            // Characters + the English work title are cross-language long-tail
            // terms the tag set often lacks — mirror of the controller.
            const maKeywords = [...new Set([
                ...keywords, ...captionKeywords,
                ...(ma.caption?.characters || []),
                ma.caption?.source_work, ma.caption?.source_work_en,
            ].filter(Boolean))];
            og = {
                type: 'website',
                title: `${maTitle} | Uwubox`,
                description,
                url: canonicalUrl,
                images: imageUrl ? [{ url: imageUrl, width: ma.meta?.width, height: ma.meta?.height }] : [],
            };
            jsonLd = [{
                '@context': 'https://schema.org',
                '@type': 'ImageObject',
                name: maTitle,
                contentUrl: imageUrl,
                description,
                creator: { '@type': 'Person', name: creatorName },
                width: ma.meta?.width,
                height: ma.meta?.height,
                ...(maKeywords.length > 0 && { keywords: maKeywords.join(', ') }),
            }, breadcrumbJsonLd(labels.crumb, '/m', maTitle)];
            adultContent = ma.caption?.rating === 'explicit';
        } else if (user && $page.component.startsWith('users/')) {
            // User profile page
            const displayName = user.display_name || user.name || 'User';
            const username = user.username ? `@${user.username}` : '';
            title = username ? `${displayName} (${username})` : displayName;
            description = user.bio
                ? truncate(user.bio, 160)
                : `${displayName}'s profile on Uwubox — ${user.posts_count || 0} posts, ${user.media_assets_count || 0} images`;
            og = {
                type: 'profile',
                title: `${title} | Uwubox`,
                description,
                url: canonicalUrl,
                profile: { username: user.username },
            };
            jsonLd = null;
        } else if (selectedTags && $page.component === 'tags/index') {
            // Tags page with selection — landing page for a topic.
            // Mirror of TagsController#index's landing meta; keep in sync.
            const names = String(selectedTags)
                .split(',').map(s => s.replaceAll('_', ' ').trim()).filter(Boolean).join(' + ');
            const nPosts = $page.props.posts_pagy?.count ?? 0;
            const nImages = $page.props.media_assets_pagy?.count ?? 0;
            const st = $page.props.selected_tags || [];
            const primaryAlias = st.length === 1 ? (st[0].primary_alt_name || null) : null;
            const baseTitle = primaryAlias ? `${names}・${primaryAlias}` : names;
            const labels = serpLabels('meme', baseTitle);
            const aliasLine = st.length === 1 ? (st[0].alt_names || []).slice(0, 3) : [];
            const aliasPart = aliasLine.length > 0 ? `別名：${aliasLine.join('、')}。` : '';
            title = `${baseTitle} ${labels.suffix}`;
            description = `${names}：${nPosts} 篇貼文、${nImages} 張圖片。${aliasPart}Uwubox 上與「${names}」相關的迷因、copypasta 與圖片合集。`;
            og = {
                type: 'website',
                title: `${baseTitle} | Uwubox`,
                description,
                url: canonicalUrl,
            };
            jsonLd = [breadcrumbJsonLd(labels.crumb, '/m', baseTitle)];
        } else if ($page.component === 'collections/show' && $page.props.collection) {
            // Mirror of CollectionsController#set_og_tags_for — keep in sync.
            // Without this branch the pack page fell through to the generic
            // site metadata, so the title flipped after hydration and every
            // client-side navigation to a pack shipped the wrong tags.
            const c = $page.props.collection;
            const cDesc = (c.description || '').replace(/\s+/g, ' ').trim();
            title = `${c.title}｜梗圖包`;
            description = cDesc.length > 0 ? truncate(cDesc, 160)
                : `${c.items_count} memes, hand-picked. 人工整理的梗圖包。`;
            og = { type: 'website', title, description, url: canonicalUrl };
            jsonLd = [{
                '@context': 'https://schema.org',
                '@type': 'CollectionPage',
                name: title,
                description,
                author: { '@type': 'Person',
                          name: c.user?.display_name || c.user?.name },
                mainEntity: { '@type': 'ItemList', numberOfItems: c.items_count },
            }];
        } else if ($page.component === 'tags/index') {
            // Mirror of TagsController#index (no selection) — keep in sync.
            title = 'Browse Tags 標籤總覽';
            description = "Browse all tags on Uwubox. 瀏覽全部標籤，依主題探索梗圖與複製文。";
            og = { type: 'website', title: `${title} | Uwubox`, description, url: canonicalUrl };
            jsonLd = null;
        } else if ($page.component.startsWith('posts/') || $page.component.startsWith('media_assets/')) {
            // Index pages — mirror of Posts/MediaAssetsController#index; keep in sync.
            const isImages = $page.component.startsWith('media_assets/');
            const section = isImages ? 'Meme Search 梗圖搜尋' : 'Copypasta Search 複製文搜尋';
            title = searchQ ? `${truncate(searchQ, 40)} — ${section}` : section;
            description = searchQ
                ? `Search results for "${truncate(searchQ, 100)}" on Uwubox`
                : (isImages
                    ? "Search memes and anime images by the text inside them. 梗圖搜尋——以圖內文字搜尋梗圖與動漫圖。"
                    : "Search copypasta, memes, and epic replies. 搜尋複製文、迷因文與神回覆。");
            og = { type: 'website', title: `${title} | Uwubox`, description, url: canonicalUrl };
            jsonLd = null;
        } else {
            title = $_("meta.title");
            description = $_("meta.description");
            og = { type: 'website', title: `Uwubox | ${title}`, description, url: canonicalUrl };
            jsonLd = null;
        }
    }
</script>

<MetaTags
    {title}
    titleTemplate="%s | Uwubox"
    {description}
    canonical={canonicalUrl}
    openGraph={og}
    twitter={{ cardType: og.images?.length > 0 ? 'summary_large_image' : 'summary', title: og.title, description, images: og.images }}
    additionalMetaTags={adultContent ? [{ name: 'rating', content: 'adult' }] : []}
/>

{#if jsonLd}
    {@html `<script type="application/ld+json">${JSON.stringify(jsonLd)}</script>`}
{/if}

<Tooltip.Provider>
<main>
    <Top {filters}></Top>
    <slot />
</main>
<UploadCatcher />
{#if showFooter}
    <footer class="py-6 px-4 text-center text-xs text-muted-foreground">
        <a href="mailto:contact@uwubox.me" class="hover:text-foreground transition-colors">contact@uwubox.me</a>
        <span class="mx-1.5">·</span>
        <a href="/tos" class="hover:text-foreground transition-colors">Terms</a>
        <span class="mx-1.5">·</span>
        <a href="/privacy" class="hover:text-foreground transition-colors">Privacy</a>
        <span class="mx-1.5">·</span>
        <a href="/guidelines" class="hover:text-foreground transition-colors">Guidelines</a>
    </footer>
{/if}
</Tooltip.Provider>
